import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoEngine } from "../context/RecoEngineContext";
import ChatBlock from "../components/ChatBlock";
import ChatBar from "../components/ChatBar";
import FeedbackRow from "../components/FeedbackRow";
import RefineForm from "../components/RefineForm";
import Banner from "../components/Banner";
import { useRecoEngineSession, useRecommendationApi, useQueryLocation, useAutoScroll } from "../hooks";
import { ShowBottomBar } from "../../../services/ToggleNativeBar";

const RecommendationPage = () => {
  const {
    queryObject,
    loading,
    setLoading,
    setQueryObject,
    setServicesObject,
    renderBlocks,
    setRenderBlocks,
    feedbackRow,
    setFeedbackRow,
    readyForQuery,
    setReadyForQuery,
    refineForm,
    setShowLocationModal,
  } = useRecoEngine();
  const { fetchRecommendations } = useRecommendationApi();
  const { handleQueryLocation } = useQueryLocation();

  const [t] = useTranslation("translations");
  const [isQueryLoaded, setIsQueryLoaded] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showChatBar, setShowChatBar] = useState(false);
  const [previousReadyForQuery, setPreviousReadyForQuery] = useState(false);
  const [currentQuery, setCurrentQuery] = useState<string | null>(null);
  const [sessionRestored, setSessionRestored] = useState(false);

  const {
    restoreRenderedBlocks,
    initializePage,
    addBlock,
    setRefineFormData,
    createInitialBlocks,
  } = useRecoEngineSession();

  // Load query from session storage
  useEffect(() => {
    if (queryObject.query) {
      setCurrentQuery(queryObject.query);
      return;
    }

    try {
      const queryObjectData = localStorage.getItem("queryObject");
      if (queryObjectData) {
        const parsed = JSON.parse(queryObjectData);
        setQueryObject(parsed);
        setCurrentQuery(parsed.query);
        setIsQueryLoaded(true);
      }
    } catch (error) {
      console.error("Error parsing queryObject from session storage:", error);
    }
  }, [setQueryObject]);

  useEffect(() => {
    setShowLocationModal(false);
    ShowBottomBar(false);
  }, []);

  useAutoScroll(hasInitialized, renderBlocks);

  // Initialize session and restore state on mount
  useEffect(() => {
    if (!isQueryLoaded || !queryObject.query) return;

    const pageState = initializePage();
    const { session } = pageState;

    if (pageState.hasExistingSession) {
      setFeedbackRow(true);
      setSessionRestored(true);

      if (session.servicesObject) {
        setServicesObject(session.servicesObject);
      }

      const restoredBlocks = restoreRenderedBlocks();
      const components = restoredBlocks.map(block => block.component).filter(Boolean);

      setRenderBlocks(components);
      setShowBanner(pageState.showBanner);
      setShowChatBar(pageState.showChatBar);
      setReadyForQuery(pageState.readyForQuery);
      setLoading(false);
      setHasInitialized(true);

    } else {
      // Fresh session - keep your existing logic
      setHasInitialized(true);
      setSessionRestored(false);
      if (queryObject.query) {
        const initialBlocks = createInitialBlocks(queryObject, undefined, t, addBlock);
        if (initialBlocks.length > 0) {
          setRenderBlocks(initialBlocks);
        }
      }
      setShowBanner(pageState.showBanner);
      setShowChatBar(pageState.showChatBar);
      setReadyForQuery(pageState.readyForQuery);
    }
  }, [isQueryLoaded]);

  // Handle new query from navigation
  useEffect(() => {
    if (!hasInitialized || !isQueryLoaded) return;

    // Only handle query if we have valid state data
    if (queryObject.query) {
      handleQueryLocation(
        queryObject.query,
        queryObject,
        queryObject.longitude,
        queryObject.latitude,
        queryObject.radius,
        queryObject.fsa
      );
    }
  }, [queryObject.query, hasInitialized, isQueryLoaded]);


  // Handle API calls - only if we don't have existing data
  useEffect(() => {
    if (!hasInitialized || !isQueryLoaded) return;

    if (currentQuery === queryObject.query && sessionRestored) {
      return;
    }

    setCurrentQuery(queryObject.query);

    setReadyForQuery(false);

    if (!queryObject.query || !queryObject.query.trim()) {
      setReadyForQuery(true);
      return;
    }

    fetchRecommendations(queryObject);
  }, [
    hasInitialized,
    isQueryLoaded,
    queryObject.query
  ]);

  // Show chat bar when user explicitly wants it
  useEffect(() => {
    if (readyForQuery && hasInitialized && !previousReadyForQuery) {
      setShowChatBar(true);
    }
    setPreviousReadyForQuery(readyForQuery);
  }, [readyForQuery, hasInitialized, previousReadyForQuery]);

  return (
    <div className="recoEngine__page">
      {showBanner && <Banner text={t("RecoSearch.history_message")} closeBanner={() => setShowBanner(false)} />}
      {renderBlocks}
      {refineForm ? <RefineForm mode="edit" setRefineFormData={setRefineFormData} addBlock={addBlock} /> : null}
      {loading ? <ChatBlock loading engineResponse /> : null}
      {feedbackRow ? <FeedbackRow options={["end", "refine", "new"]} /> : null}
      {showChatBar && readyForQuery ? <ChatBar disabled={!readyForQuery} /> : null}
    </div>
  );
};

RecommendationPage.displayName = "RecommendationPage";

export default RecommendationPage;
